// src/pages/AuditLogs.jsx (or wherever you keep it)
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "./ui/Select";
import {
  ClipboardList,
  RefreshCw,
  Filter,
  User,
  Activity,
  Database,
  Hash,
  Link as LinkIcon,
  Calendar,
} from "lucide-react";
import axiosInstance from "../utils/axiosInstance";
import PaginationControls from "../components/PaginationControls";

export default function AuditLogs() {
  const navigate = useNavigate();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    username: "",
    action: "",
    model_name: "",
    object_pk: "",
    path_icontains: "",
    date_from: "",
    date_to: "",
    lang: "fa",
  });

  const [userSuggestions, setUserSuggestions] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [totalPages, setTotalPages] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  const manage =
    typeof window !== "undefined"
      ? JSON.parse(localStorage.getItem("user") || "{}")
      : {};
  const isAdmin = manage?.role?.toLowerCase() === "admin";

  useEffect(() => {
    if (!isAdmin) navigate("/Forbidden");
  }, [isAdmin, navigate]);

  const filterLabels = useMemo(() => ({
    create: "ایجاد",
    update: "به‌روزرسانی",
    delete: "حذف",
    login: "ورود",
    logout: "خروج",
    view: "مشاهده",
    custom: "سفارشی",
    company_delete: "حذف شرکت",
    role_change: "تغییر نقش کاربر",
    creds_replace: "تغییر اطلاعات ورود سامانه جامع تجارت",
    order_submit: "ارسال ثبت سفارش",
    sync_started: "شروع همگام‌سازی",
    sync_completed: "پایان همگام‌سازی",
    bulk_delete: "حذف گروهی",
    import: "ورود اطلاعات",
    document_upload: "بارگذاری سند",
    document_delete: "حذف سند",
    download: "دریافت سند",
    sms_send: "ارسال پیامک",
    sms_completed: "پایان ارسال پیامک",
    password_change: "تغییر رمز عبور",
    login_failed: "ورود ناموفق",
  }), []);

  const modelLabels = useMemo(() => ({
    Cottage: "اظهارنامه",
    CottageGoods: "کالای اظهارنامه",
    Performa: "ثبت سفارش",
    Costumer: "مشتری",
    CustomUser: "کاربر",
    Invoice: "فاکتور",
    InvoiceItem: "کالای فاکتور",
    ProformaInvoice: "پروفورما",
    ProformaInvoiceItem: "کالای پروفورما",
    Check: "چک",
    ExportedCottages: "اظهارنامه صادراتی",
    PreCottage: "تعیین منشأ ارز",
    CottageDocument: "سند اظهارنامه",
    Expenses: "هزینه اظهارنامه",
    Company: "شرکت",
    NtswCreds: "اطلاعات ورود سامانه جامع تجارت",
    SyncJob: "همگام‌سازی",
    SmsCampaign: "کمپین پیامکی",
    SmsMessageTemplate: "قالب پیامک",
    Document: "سند",
    LetterTemplate: "قالب نامه",
    Letter: "نامه",
    Seller: "فروشنده",
    Buyer: "خریدار",
    DocumentCategory: "دسته‌بندی سند",
    DocumentSubCategory: "زیردسته سند",
    CurrencyAllocation: "تخصیص ارز",
    ProformaCurrencyPurchase: "خرید ارز ثبت سفارش",
    Storage: "انبار",
    Events: "رویداد تقویم",
  }), []);

  const toParams = (obj) =>
    Object.fromEntries(
      Object.entries(obj).filter(
        ([_, v]) => v !== "" && v !== null && v !== undefined
      )
    );

  const usernameOptions = useMemo(
    () =>
      userSuggestions.map((u) => ({ value: u.username, label: u.username })),
    [userSuggestions]
  );

  const actionOptions = useMemo(
    () =>
      Object.entries(filterLabels).map(([value, label]) => ({ value, label })),
    [filterLabels]
  );

  const modelOptions = useMemo(
    () =>
      Object.entries(modelLabels).map(([value, label]) => ({ value, label })),
    [modelLabels]
  );

  const selectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        borderRadius: 14,
        borderColor: state.isFocused ? "#cbd5e1" : "#e2e8f0",
        boxShadow: state.isFocused ? "0 0 0 4px rgba(59,130,246,0.12)" : "none",
        minHeight: 44,
        backgroundColor: "white",
      }),
      valueContainer: (base) => ({ ...base, paddingInline: 10 }),
      input: (base) => ({ ...base, direction: "rtl" }),
      singleValue: (base) => ({ ...base, direction: "rtl" }),
      placeholder: (base) => ({ ...base, direction: "rtl", color: "#94a3b8" }),
      menu: (base) => ({ ...base, borderRadius: 14, overflow: "hidden" }),
      option: (base, state) => ({
        ...base,
        direction: "rtl",
        backgroundColor: state.isFocused ? "#f1f5f9" : "white",
        color: "#0f172a",
      }),
    }),
    []
  );

  const fetchLogs = async () => {
    const params = { ...toParams(filters), page, page_size: pageSize };

    setLoading(true);
    try {
      const res = await axiosInstance.get("/audit-logs", { params });
      setLogs(res.data.results || []);
      setTotalPages(res.data.total_pages || 1);
      setHasNext(!!res.data.next);
      setHasPrevious(!!res.data.previous);
    } catch (err) {
      console.log(err);
      setLogs([]);
      setTotalPages(1);
      setHasNext(false);
      setHasPrevious(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, pageSize]);

  const loadUsers = async () => {
    if (usersLoaded) return;
    try {
      const res = await axiosInstance.get("/accounts/users");
      setUserSuggestions(res.data || []);
      setUsersLoaded(true);
    } catch (err) {
      console.log(err);
    }
  };

  const resetFilters = () => {
    setFilters({
      username: "",
      action: "",
      model_name: "",
      object_pk: "",
      path_icontains: "",
      date_from: "",
      date_to: "",
      lang: "fa",
    });
    setPage(1);
  };

  const activeFiltersCount = useMemo(() => {
    const { lang, ...rest } = filters;
    return Object.values(rest).filter((v) => v !== "").length;
  }, [filters]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" dir="rtl">
      <div className="mx-auto max-w-7xl px-4 pb-10 pt-28 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-2 text-right">
            <div className="inline-flex items-center gap-2">
              <div className="grid h-10 w-10 place-items-center rounded-2xl bg-white shadow-sm ring-1 ring-slate-200">
                <ClipboardList className="h-5 w-5 text-slate-700" />
              </div>
              <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">
                لاگ کاربران
              </h1>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                صفحه {page} از {totalPages}
              </span>
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                {pageSize} ردیف در هر صفحه
              </span>
              {activeFiltersCount ? (
                <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                  {activeFiltersCount} فیلتر فعال
                </span>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={fetchLogs}
              className="inline-flex items-center gap-2 rounded-2xl bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50"
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />
              بروزرسانی
            </button>

            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-slate-800"
            >
              <Filter className="h-4 w-4" />
              پاک کردن فیلترها
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 rounded-3xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
          <div className="grid grid-cols-1 gap-4 text-right sm:grid-cols-2 lg:grid-cols-3">
            {/* Username */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <User className="h-4 w-4 text-slate-500" />
                نام کاربر
              </label>
              <Select
                value={
                  filters.username
                    ? { value: filters.username, label: filters.username }
                    : null
                }
                onChange={(selected) => {
                  setPage(1);
                  setFilters((prev) => ({
                    ...prev,
                    username: selected ? selected.value : "",
                  }));
                }}
                options={usernameOptions}
                onMenuOpen={loadUsers}
                placeholder="انتخاب کاربر..."
                isClearable
                styles={selectStyles}
              />
            </div>

            {/* Action */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Activity className="h-4 w-4 text-slate-500" />
                نوع عملیات
              </label>
              <Select
                value={
                  filters.action
                    ? {
                        value: filters.action,
                        label: filterLabels[filters.action] || filters.action,
                      }
                    : null
                }
                onChange={(selected) => {
                  setPage(1);
                  setFilters((prev) => ({
                    ...prev,
                    action: selected ? selected.value : "",
                  }));
                }}
                options={actionOptions}
                placeholder="انتخاب عملیات..."
                isClearable
                styles={selectStyles}
              />
            </div>

            {/* Model */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Database className="h-4 w-4 text-slate-500" />
                نام مدل
              </label>
              <Select
                value={
                  filters.model_name
                    ? {
                        value: filters.model_name,
                        label:
                          modelLabels[filters.model_name] || filters.model_name,
                      }
                    : null
                }
                onChange={(selected) => {
                  setPage(1);
                  setFilters((prev) => ({
                    ...prev,
                    model_name: selected ? selected.value : "",
                  }));
                }}
                options={modelOptions}
                placeholder="انتخاب مدل..."
                isClearable
                styles={selectStyles}
              />
            </div>

            {/* object_pk */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Hash className="h-4 w-4 text-slate-500" />
                شناسه (PK)
              </label>
              <input
                value={filters.object_pk}
                onChange={(e) => {
                  setPage(1);
                  setFilters((prev) => ({
                    ...prev,
                    object_pk: e.target.value,
                  }));
                }}
                placeholder="مثلاً 12345"
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:ring-4 focus:ring-blue-100"
              />
            </div>

            {/* path */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <LinkIcon className="h-4 w-4 text-slate-500" />
                آدرس مسیر (شامل)
              </label>
              <input
                value={filters.path_icontains}
                onChange={(e) => {
                  setPage(1);
                  setFilters((prev) => ({
                    ...prev,
                    path_icontains: e.target.value,
                  }));
                }}
                placeholder="مثلاً /api/..."
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:ring-4 focus:ring-blue-100"
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Calendar className="h-4 w-4 text-slate-500" />
                  تاریخ از
                </label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => {
                    setPage(1);
                    setFilters((prev) => ({
                      ...prev,
                      date_from: e.target.value,
                    }));
                  }}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:ring-4 focus:ring-blue-100"
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Calendar className="h-4 w-4 text-slate-500" />
                  تاریخ تا
                </label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => {
                    setPage(1);
                    setFilters((prev) => ({
                      ...prev,
                      date_to: e.target.value,
                    }));
                  }}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:ring-4 focus:ring-blue-100"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Pagination (use your component) */}
        <div className="mt-6">
          <PaginationControls
            currentPage={page}
            totalPages={totalPages}
            pageSize={pageSize}
            pageSizeOptions={[5, 10, 20, 50]}
            hasNext={hasNext}
            hasPrevious={hasPrevious}
            onPageChange={(e) => setPage(Number(e.target.value))}
            onNextPage={() => setPage((p) => p + 1)}
            onPreviousPage={() => setPage((p) => Math.max(1, p - 1))}
            onPageSizeChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          />
        </div>

        {/* Table */}
        <div className="mt-4 overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-200">
          <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-800">
                نتایج
              </span>
              {loading ? (
                <span className="text-xs text-slate-500">در حال دریافت...</span>
              ) : (
                <span className="text-xs text-slate-500">
                  {logs.length} ردیف
                </span>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-right text-sm" dir="rtl">
              <thead className="bg-slate-50">
                <tr className="text-slate-600">
                  <th className="whitespace-nowrap px-4 py-3 font-semibold">
                    #
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 font-semibold">
                    کاربر
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 font-semibold">
                    عملیات
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 font-semibold">
                    مدل
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 font-semibold">
                    شناسه
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 font-semibold">
                    مسیر
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 font-semibold">
                    زمان
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 font-semibold">
                    تغییرات / جزئیات
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {logs.length ? (
                  logs.map((log, index) => (
                    <tr key={log.id} className="hover:bg-slate-50/70">
                      <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                        {(page - 1) * pageSize + index + 1}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-slate-800">
                        {log.user_display || "-"}
                        {log.company_id ? (
                          <div className="text-xs text-slate-500">
                            شرکت {Number(log.company_id).toLocaleString("fa-IR")}
                          </div>
                        ) : null}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                          {log.action_display || "-"}
                        </span>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-slate-800">
                        {log.model_display || "-"}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                        {log.object_pk || "-"}
                      </td>

                      <td className="max-w-[420px] px-4 py-3 text-slate-700">
                        <div className="truncate" title={log.path || ""}>
                          {log.path || "-"}
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                        {log.occurred_at_jalali || "-"}
                      </td>

                      <td className="px-4 py-3 text-slate-700">
                        {log.changes_fa?.length ? (
                          <ul className="space-y-1">
                            {log.changes_fa.map((c, i) => (
                              <li key={i} className="leading-6">
                                <span className="font-medium">{c["فیلد"]}</span>{" "}
                                از{" "}
                                <span className="font-semibold">
                                  {String(c["از"] ?? "-")}
                                </span>{" "}
                                به{" "}
                                <span className="font-semibold">
                                  {String(c["به"] ?? "-")}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                        {log.details_fa ? (
                          <span className="leading-6">{log.details_fa}</span>
                        ) : !log.changes_fa?.length ? (
                          <span className="text-slate-400">-</span>
                        ) : null}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      className="px-4 py-10 text-center text-slate-500"
                      colSpan={8}
                    >
                      {loading
                        ? "در حال دریافت داده‌ها..."
                        : "هیچ داده‌ای یافت نشد."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom Pagination (optional but nice UX) */}
        <div className="mt-6">
          <PaginationControls
            currentPage={page}
            totalPages={totalPages}
            pageSize={pageSize}
            pageSizeOptions={[5, 10, 20, 50]}
            hasNext={hasNext}
            hasPrevious={hasPrevious}
            onPageChange={(e) => setPage(Number(e.target.value))}
            onNextPage={() => setPage((p) => p + 1)}
            onPreviousPage={() => setPage((p) => Math.max(1, p - 1))}
            onPageSizeChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          />
        </div>
      </div>
    </div>
  );
}
